
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Use this guide to learn how to integrate with the Link Webview JavaScript SDK',
  preloadImages: [{
    src: '/assets/img/products/link-webview-screenshot.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Link Webview SDK' subtitle='Reference for integrating with the Link Webview JavaScript SDK' mdxType="Header" />
    <Callout warning mdxType="Callout">
      <p>{`  Using webviews to present Link is deprecated. If you're currently integrating this way, we recommend migrating to `}<a parentName="p" {...{
          "href": "/docs/link/hosted-link"
        }}>{`Hosted Link`}</a>{` or one of Plaid's official SDKs for
`}<a parentName="p" {...{
          "href": "/docs/link/android/"
        }}>{`Android`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/link/ios/"
        }}>{`iOS`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/link/react-native/"
        }}>{`React Native`}</a>{`, and `}<a parentName="p" {...{
          "href": "/docs/link/flutter/"
        }}>{`Flutter`}</a>{`.`}</p>
    </Callout>
    <Callout danger mdxType="Callout">
      <p>{`  All Webview-based integrations need to extend the Webview handler for redirects in order to support connections to Chase. This can be accomplished with code samples for `}<a parentName="p" {...{
          "href": "https://github.com/plaid/plaid-link-examples/blob/master/webviews/wkwebview/wkwebview/LinkViewController.swift#L56-L72"
        }}>{`iOS`}</a>{` and `}<a parentName="p" {...{
          "href": "https://github.com/plaid/plaid-link-examples/blob/master/webviews/android/LinkWebview/app/src/main/java/com/example/linkwebview/MainActivity.kt#L94-L157"
        }}>{`Android`}</a>{`. For more details, see `}<a parentName="p" {...{
          "href": "/docs/link/oauth/#extending-webview-instances-to-support-certain-institutions"
        }}>{`Extending webview instances to support certain institutions`}</a>{` in the OAuth Guide and the `}<a parentName="p" {...{
          "href": "https://plaid.docsend.com/view/h3qdupjusiwyjvv5"
        }}>{`in-process Webview deprecation notice`}</a>{`. Webview-based integrations that do not extend the Webview handler are not supported by Chase and may be blocked by Chase in the future.`}</p>
      <p>{`  If your integration does not connect to Chase (for example, because you use only Identity Verification, Document Income, or Payroll Income, or you do not support end users in the US) you may ignore this warning.`}</p>
    </Callout>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`To integrate and use Plaid Link inside a Webview, we recommend starting with one of our sample Webview apps:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/plaid/plaid-link-examples/tree/master/webviews/wkwebview"
        }}>{`iOS WKWebView`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/plaid/plaid-link-examples/tree/master/webviews/android"
        }}>{`Android Webview`}</a></li>
    </ul>
    <p>{`Each example app is runnable (on both simulators and devices) and includes code to initialize Link and process events sent from Link to your app via HTTP redirects.`}</p>
    <h4 {...{
      "id": "installation"
    }}>{`Installation`}</h4>
    <p>{`Link is optimized to work within Webviews, including on iOS and Android. The Link initialization URL to use for Webviews is:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "switcher=false #Link Webview initialization URL",
        "switcher": "false",
        "#Link": true,
        "Webview": true,
        "initialization": true,
        "URL": true
      }}>{`https://cdn.plaid.com/link/v2/stable/link.html
`}</code></pre>
    <p>{`The Link configuration options for a Webview integration are passed via querystring rather than via a client-side JavaScript call. See the `}<a parentName="p" {...{
        "href": "#create"
      }}>{`create`}</a>{` section below for details on the available initialization parameters.`}</p>
    <h5 {...{
      "id": "communication-between-link-and-your-app"
    }}>{`Communication between Link and your app`}</h5>
    <p>{`Communication between the Webview and your app is handled by HTTP redirects rather than client-side JavaScript callbacks. These redirects should be intercepted by your app. The `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-link-examples"
      }}>{`example apps`}</a>{` include sample code to do this.`}</p>
    <p>{`All redirect URLs have the scheme `}<inlineCode parentName="p">{`plaidlink`}</inlineCode>{`. The event type is communicated via the URL host and data is passed via the querystring.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "switcher=false #HTTP redirect scheme",
        "switcher": "false",
        "#HTTP": true,
        "redirect": true,
        "scheme": true
      }}>{`plaidlink://
`}</code></pre>
    <p>{`There are three supported events, `}<a parentName="p" {...{
        "href": "#connected"
      }}><inlineCode parentName="a">{`connected`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "#exit"
      }}><inlineCode parentName="a">{`exit`}</inlineCode></a>{`, and `}<a parentName="p" {...{
        "href": "#event"
      }}><inlineCode parentName="a">{`event`}</inlineCode></a>{`, which are documented below.`}</p>
    <h4 {...{
      "id": "create"
    }}><inlineCode parentName="h4">{`create()`}</inlineCode></h4>
    <Schema type='link' sdk='webview' method='create' mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-text",
          "metastring": "switcher=false #Create example",
          "switcher": "false",
          "#Create": true,
          "example": true
        }}>{`https://cdn.plaid.com/link/v2/stable/link.html
  ?isWebview=true
  &token="GENERATED_LINK_TOKEN"
  &receivedRedirectUri=
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "connected"
    }}><inlineCode parentName="h4">{`connected`}</inlineCode></h4>
    <Schema type='link' sdk='webview' method='connected' mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-text",
          "metastring": "switcher=false #Connected event example",
          "switcher": "false",
          "#Connected": true,
          "event": true,
          "example": true
        }}>{`plaidlink://connected
  ?public_token=public-sandbox-fb7cca4a-82e6-4707
  &institution_id=ins_3
  &institution_name=Chase
  &accounts='[{"_id":"QPO8Jo8vdDHMepg41PBwckXm4KdK1yUdmXOwK", "meta": {"name":"Plaid Checking", "number": "0000"}, "subtype": "checking", "type": "depository" }]'
  &link_session_id=79e772be-547d-4c9c-8b76-4ac4ed4c441a
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "switcher=false #accounts schema example",
          "switcher": "false",
          "#accounts": true,
          "schema": true,
          "example": true
        }}>{`"accounts": [
  {
    "_id": "ygPnJweommTWNr9doD6ZfGR6GGVQy7fyREmWy",
    "meta": {
      "name": "Plaid Checking",
      "number": "0000"
    },
    "type": "depository",
    "subtype": "checking",
    "verification_status": null
  },
  {
    "_id": "9ebEyJAl33FRrZNLBG8ECxD9xxpwWnuRNZ1V4",
    "meta": {
      "name": "Plaid Saving",
      "number": "1111"
    },
    "type": "depository",
    "subtype": "savings"
  }
  ...
]
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "exit"
    }}><inlineCode parentName="h4">{`exit()`}</inlineCode></h4>
    <Schema type='link' sdk='webview' method="exit" mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-text",
          "metastring": "switcher=false #onExit example",
          "switcher": "false",
          "#onExit": true,
          "example": true
        }}>{`plaidlink://exit
  ?status=requires_credentials
  &error_type=ITEM_ERROR
  &error_code=ITEM_LOGIN_REQUIRED
  &error_message=the%20login%20details%20of%20this%20item%20have%20changed%20%28credentials%2C%20MFA%2C%20or%20required%20user%20action%29%20and%20a%20user%20login%20is%20required%20to%20update%20this%20information.%20use%20Link%27s%20update%20mode%20to%20restore%20the%20item%20to%20a%20good%20state
  &institution_id=ins_3
  &institution_name=Chase
  &link_session_id=79e772be-547d-4c9c-8b76-4ac4ed4c441a
  &request_id=m8MDnv9okwxFNBV
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "event"
    }}><inlineCode parentName="h4">{`event`}</inlineCode></h4>
    <Schema type='link' sdk='webview' method="event" mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-text",
          "metastring": "switcher=false #event example",
          "switcher": "false",
          "#event": true,
          "example": true
        }}>{`plaidlink://event
  ?event_name=SELECT_INSTITUTION
  &error_type=ITEM_ERROR
  &error_code=ITEM_LOGIN_REQUIRED
  &error_message=the%20login%20details%20of%20this%20item%20have%20changed%20%28credentials%2C%20MFA%2C%20or%20required%20user%20action%29%20and%20a%20user%20login%20is%20required%20to%20update%20this%20information.%20use%20Link%27s%20update%20mode%20to%20restore%20the%20item%20to%20a%20good%20state
  &exit_status
  &institution_id=ins_55
  &institution_name=HSBC
  &institution_search_query=h
  &mfa_type
  &view_name=ERROR
  &request_id
  &link_session_id=821f45a8-854a-4dbb-8e5f-73f75350e7e7
  &timestamp=2018-10-05T15%3A22%3A50.542Z
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "oauth"
    }}>{`OAuth`}</h4>
    <p>{`Using Plaid Link with an OAuth flow requires some additional setup instructions. For details, see the `}<a parentName="p" {...{
        "href": "/docs/link/oauth"
      }}>{`OAuth Guide`}</a>{`.`}</p>
    <h4 {...{
      "id": "supported-platforms"
    }}>{`Supported platforms`}</h4>
    <p>{`Plaid officially supports WKWebView on iOS and Chrome WebView on Android. As with `}<a parentName="p" {...{
        "href": "/docs/link/web/#supported-browsers"
      }}>{`Link web`}</a>{`, Plaid does not support versions that are no longer receiving patch updates, or that have been assigned official end of life (EOL) or end of support (EOS) status.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "installation",
        level: 4,
        title: "Installation",
      },
{
        id: "create",
        level: 4,
        title: "create()",
      },
{
        id: "connected",
        level: 4,
        title: "connected",
      },
{
        id: "exit",
        level: 4,
        title: "exit()",
      },
{
        id: "event",
        level: 4,
        title: "event",
      },
{
        id: "oauth",
        level: 4,
        title: "OAuth",
      },
{
        id: "supported-platforms",
        level: 4,
        title: "Supported platforms",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
